import {
  List,
  ListItemIcon,
  ListItemText,
  Box,
  ListItemButton,
  Tooltip,
} from "@mui/material";
import {
  SettingsOutlined,
  ReceiptLongOutlined,
  PercentOutlined,
  AccountBalanceWalletOutlined,
  CodeOutlined,
} from "@mui/icons-material";
import { SidebarProps } from "../utils/types";
import { useLocation, useNavigate } from "react-router";
import { useTheme } from "@mui/material";

const menuItems = [
  { text: "Rates", icon: <PercentOutlined />, path: "/rates" },
  { text: "Transactions", icon: <ReceiptLongOutlined />, path: "/transactions" },
  { text: "Balance", icon: <AccountBalanceWalletOutlined />, path: "/balance" },
  { text: "Settings", icon: <SettingsOutlined />, path: "/settings" },
  { text: "API docs", icon: <CodeOutlined />, path: "/instructions" },
];

const Sidebar = ({ isMobile, open, setOpen }: SidebarProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Box
      component="nav"
      aria-label="Main navigation"
      height={isMobile ? "calc(100% - 8px)" : "100%"}
      flexDirection="column"
      justifyContent="space-between"
      position={isMobile ? "absolute" : "relative"}
      sx={{
        transition: "width 0.32s cubic-bezier(0.4, 0, 0.2, 1)",
        backgroundColor:
          isMobile && open ? theme.palette.background.paper : "transparent",
        border: isMobile && open ? `1px solid ${theme.palette.divider}` : 0,
        borderRadius: isMobile && open ? "20px" : 0,
        boxShadow:
          isMobile && open ? "16px 0 48px rgba(8, 12, 24, 0.25)" : "none",
        overflow: "hidden",
        zIndex: 20,
        display: "flex",
        left: isMobile ? 4 : 0,
        top: isMobile ? 4 : 0,
        flexShrink: 0,
      }}
      width={open ? 264 : isMobile ? 0 : 72}
      onMouseEnter={!isMobile ? () => setOpen(true) : undefined}
      onMouseLeave={!isMobile ? () => setOpen(false) : undefined}
    >
      <List
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          gap: "6px",
          height: "100%",
          // Tall phone screens leave more room than the items need; without a
          // scroll container and minHeight: 0 the list stretches instead.
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          paddingTop: "14px",
          // The floating menu button sits fixed bottom-left (50px + 20px gap);
          // without this room it would cover the last entry.
          paddingBottom: isMobile ? "84px" : "14px",
          px: open ? 1.25 : 0.75,
        }}
      >
        {menuItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);

          return (
            <Tooltip
              key={item.path}
              title={open ? "" : item.text}
              placement="right"
            >
              <ListItemButton
                selected={isActive}
                aria-current={isActive ? "page" : undefined}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setOpen(false);
                }}
                sx={{
                  position: "relative",
                  color: isActive
                    ? theme.palette.accentText
                    : theme.palette.text.secondary,
                  width: "100%",
                  // MUI gives ListItemButton flex: 1 1 auto, which in a column
                  // stretches every entry over the full height of the drawer.
                  flex: "0 0 auto",
                  minHeight: 50,
                  borderRadius: "13px",
                  px: 1.4,
                  overflow: "hidden",
                  "&.Mui-selected": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(99, 91, 255, 0.16)"
                        : "rgba(99, 91, 255, 0.10)",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 12,
                      bottom: 12,
                      width: 3,
                      borderRadius: "0 4px 4px 0",
                      backgroundColor: theme.palette.primary.main,
                    },
                  },
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.06)"
                        : "rgba(23, 32, 51, 0.05)",
                    color: isActive
                      ? theme.palette.primary.light
                      : theme.palette.text.primary,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: "inherit",
                    minWidth: 0,
                    width: 40,
                    "& svg": { fontSize: 23 },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    whiteSpace: "nowrap",
                    opacity: open ? 1 : 0,
                    maxWidth: open ? 170 : 0,
                    overflow: "hidden",
                    transition: "max-width 0.3s ease, opacity 0.3s ease",
                    color: "inherit",
                    ml: open ? 1.25 : 0,
                    "& .MuiTypography-root": {
                      color: "inherit",
                      fontWeight: isActive ? 680 : 560,
                      fontSize: 14,
                    },
                  }}
                />
              </ListItemButton>
            </Tooltip>
          );
        })}
      </List>
    </Box>
  );
};

export default Sidebar;
